import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { Loader2, CheckCircle, AlertTriangle, FileText, Hash, Calendar, User } from 'lucide-react';
import { ConsentForm } from '../types/encounter';

const SignedConsentView: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [form, setForm] = useState<ConsentForm | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (token) {
            fetchFormDetails();
        }
    }, [token]);

    const fetchFormDetails = async () => {
        try {
            const response = await fetch(API_ENDPOINTS.ENCOUNTER_CONSENT.GET_PUBLIC(token!));
            if (response.ok) {
                const data = await response.json();
                setForm(data);
            } else {
                setError('Document not found or link expired');
            }
        } catch (err) {
            setError('Failed to load document');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    if (error || !form) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Document</h2>
                    <p className="text-gray-600">{error || 'Document not found'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col">
            {/* Header */}
            <header className="bg-white shadow-sm px-6 py-4 sticky top-0 z-10">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center shadow-sm">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-gray-900 leading-tight">Signed Document</h1>
                            <div className="text-sm text-gray-500 truncate max-w-xs sm:max-w-md">
                                {form.filename}
                            </div>
                        </div>
                    </div>
                    <div className="hidden sm:flex items-center space-x-2 text-xs text-green-700 bg-green-50 px-3 py-1 rounded-full border border-green-200">
                        <CheckCircle className="w-3 h-3" />
                        <span>Verified & Signed</span>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 max-w-6xl w-full mx-auto p-4 sm:p-6 flex flex-col lg:flex-row gap-6">
                {/* PDF Viewer (Left/Top) */}
                <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[500px] lg:min-h-[calc(100vh-140px)] relative">
                    <iframe
                        src={`${form.url}#toolbar=0&navpanes=0&scrollbar=0`}
                        className="w-full h-full border-0 absolute inset-0"
                        title="PDF Document"
                    />
                </div>

                {/* Signature Details Panel (Right/Bottom) */}
                <div className="w-full lg:w-96 flex-shrink-0 space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-24">
                        <h3 className="text-xl font-semibold text-gray-900 mb-6 border-b pb-4">Signature Details</h3>

                        <div className="space-y-6">
                            {/* Signature Display */}
                            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-center">
                                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Digital Signature</p>
                                <div className="font-cursive text-3xl text-blue-900 py-4" style={{ fontFamily: '"Dancing Script", "Brush Script MT", cursive' }}>
                                    {form.signer_info?.name || 'Signed'}
                                </div>
                            </div>

                            {/* Metadata List */}
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <User className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">Signed By</p>
                                        <p className="text-sm text-gray-600">{form.signer_info?.name || 'Unknown'}</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">Date Signed</p>
                                        <p className="text-sm text-gray-600">
                                            {form.signed_at ? new Date(form.signed_at).toLocaleString() : '-'}
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-sm font-medium text-gray-900">Document Hash</p>
                                    <div className="text-xs text-gray-500 font-mono break-all bg-gray-50 p-2 rounded mt-1 border border-gray-200">
                                        {form.signature_hash || '-'}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start mt-4">
                                <div className="w-5 h-5 flex items-center justify-center mr-3 mt-0.5">
                                    <span className="text-xs font-bold text-gray-400 border border-gray-400 rounded px-0.5">IP</span>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">Signer IP</p>
                                    <p className="text-sm text-gray-600 font-mono">{form.signer_info?.ip || 'Not recorded'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="pt-6 border-t border-gray-100">
                            <div className="flex items-center justify-center text-xs text-gray-400">
                                <CheckCircle className="w-3 h-3 mr-1.5" />
                                Cryptographically Verified
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SignedConsentView;
